import { useMemo, useState } from 'react';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import LineChart from '../../../reusable/LineChart/LineChart';
import DataTable from '../../../reusable/DataTable/DataTable';
import SymbolFlag from '../../../reusable/SymbolFlag/SymbolFlag';
import AnalysisBarChart from './AnalysisBarChart';
import { NA, signedUsd, usd } from '../../../utilities/format';
import { t } from '../../../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const SUB_TABS = [
  'Long / Short Balance',
  'Results by Days',
  'Long / Short Comparison',
  'Trading Days Analysis',
  'Results by Position Size',
  'Results by Trade Duration',
  'Results by Open Hour',
];

const num = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
const bare = (v) => String(v ?? '').replace('.pro', '');

export default function AnalysisSubSection({ data }) {
  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS[0]);

  const lsComp = data?.long_short_comp;
  const closeOpen = data?.close_open;
  const dayWise = data?.day_wise_data ?? {};
  const instruments = data?.result_by_instruments ?? [];
  const tradingDays = data?.trading_days_analysis;
  const resultsByLots = data?.results_by_lots ?? {};
  const tradeDuration = data?.result_by_trade_duration;
  const openHour = data?.result_by_open_hour;

  // 1. Long / Short Comparison Cards
  const tone = (v) => (num(v) == null ? undefined : num(v) >= 0 ? 'up' : 'down');
  const pct = (v) => (num(v) == null ? '--' : `${num(v).toFixed(2)}%`);
  const ratio = (v) => (num(v) == null ? '--' : num(v).toFixed(2));
  const lsCards = [
    {
      title: 'No. of Trades',
      long: lsComp?.number_of_long_trade ?? '--',
      short: lsComp?.number_of_Short_trade ?? '--',
      icon: 'table-rows',
    },
    {
      title: 'Results',
      long: signedUsd(closeOpen?.long_result, { decimals: 2 }),
      short: signedUsd(closeOpen?.short_result, { decimals: 2 }),
      longTone: tone(closeOpen?.long_result),
      shortTone: tone(closeOpen?.short_result),
      icon: 'chart-profit',
    },
    {
      title: 'Win Rate',
      long: pct(lsComp?.long_win_rate),
      short: pct(lsComp?.short_win_rate),
      icon: 'metrics-statistics',
    },
    {
      title: 'Average Profit',
      long: signedUsd(lsComp?.long_avg_profit, { decimals: 2 }),
      short: signedUsd(lsComp?.short_avg_profit, { decimals: 2 }),
      longTone: tone(lsComp?.long_avg_profit),
      shortTone: tone(lsComp?.short_avg_profit),
      icon: 'stat-payouts',
    },
    {
      title: 'RRR',
      long: ratio(lsComp?.long_rrr),
      short: ratio(lsComp?.short_rrr),
      icon: 'metrics-session',
    },
  ];

  // Long & Short curve series
  const longCurveData = useMemo(() => {
    const balances = lsComp?.Long_chart?.balance ?? [];
    return balances.map((val, i) => ({
      x: lsComp?.Long_chart?.dates?.[i] ?? `T${i + 1}`,
      y: Number(val) || 0,
    }));
  }, [lsComp?.Long_chart]);

  const shortCurveData = useMemo(() => {
    const balances = lsComp?.short_chart?.balance ?? [];
    return balances.map((val, i) => ({
      x: lsComp?.short_chart?.dates?.[i] ?? `T${i + 1}`,
      y: Number(val) || 0,
    }));
  }, [lsComp?.short_chart]);

  // 2. Results by Days
  const daysBarDataOpen = useMemo(() => {
    return Object.keys(dayWise).map((day) => ({
      label: day.slice(0, 3),
      value: Number(dayWise[day]?.open) || 0,
    }));
  }, [dayWise]);

  const daysBarDataClose = useMemo(() => {
    return Object.keys(dayWise).map((day) => ({
      label: day.slice(0, 3),
      value: Number(dayWise[day]?.close) || 0,
    }));
  }, [dayWise]);

  const daysTableRows = useMemo(() => {
    return Object.keys(dayWise).map((day) => ({
      id: day,
      day,
      open: Number(dayWise[day]?.open) || 0,
      close: Number(dayWise[day]?.close) || 0,
      total: (Number(dayWise[day]?.open) || 0) + (Number(dayWise[day]?.close) || 0),
    }));
  }, [dayWise]);

  const daysColumns = [
    { key: 'day', header: 'Day', width: 1.2, value: (r) => t(r.day) },
    {
      key: 'open',
      header: 'Open Result',
      width: 1,
      tone: (r) => (r.open >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.open),
    },
    {
      key: 'close',
      header: 'Close Result',
      width: 1,
      tone: (r) => (r.close >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.close),
    },
    {
      key: 'total',
      header: 'Total Net',
      width: 1,
      tone: (r) => (r.total >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.total),
    },
  ];

  // 3. Results by Instruments
  const instrumentBarData = useMemo(() => {
    return instruments.map(([sym, item]) => ({
      label: bare(sym),
      value: Number(item?.results) || 0,
    }));
  }, [instruments]);

  const instrumentTableRows = useMemo(() => {
    return instruments.map(([sym, item]) => ({
      id: sym,
      symbol: bare(sym),
      rawSymbol: sym,
      trades: item?.number_of_trade ?? 0,
      result: Number(item?.results) || 0,
      longTrades: item?.long_trade ?? 0,
      longResult: Number(item?.long_result) || 0,
      shortTrades: item?.short_trade ?? 0,
      shortResult: Number(item?.short_result) || 0,
    }));
  }, [instruments]);

  const instrumentColumns = [
    {
      key: 'symbol',
      header: 'Instrument',
      width: 1.5,
      render: (r) => (
        <span className="am-analysis__sym-cell">
          <SymbolFlag symbol={r.symbol} />
          <span>{r.symbol}</span>
        </span>
      ),
    },
    { key: 'trades', header: 'Trades', width: 0.7, sortable: true },
    {
      key: 'result',
      header: 'Net Result',
      width: 1,
      tone: (r) => (r.result >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.result),
      sortable: true,
    },
    { key: 'longTrades', header: 'Long Trades', width: 0.8 },
    {
      key: 'longResult',
      header: 'Long Result',
      width: 1,
      tone: (r) => (r.longResult >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.longResult),
    },
    { key: 'shortTrades', header: 'Short Trades', width: 0.8 },
    {
      key: 'shortResult',
      header: 'Short Result',
      width: 1,
      tone: (r) => (r.shortResult >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.shortResult),
    },
  ];

  // 4. Trading Days Analysis
  const tradingDayCards = [
    { label: 'No. of days', value: tradingDays?.number_of_days ?? '--', icon: 'metrics-calendar' },
    { label: 'Avg trades / day', value: tradingDays?.number_of_trade ?? '--', icon: 'table-rows' },
    { label: 'Positive days', value: tradingDays?.positive_days ?? '--', tone: 'up', icon: 'metrics-objectives' },
    { label: 'Avg positive day', value: signedUsd(tradingDays?.avg_positive_days), tone: 'up', icon: 'chart-profit' },
    { label: 'Negative days', value: tradingDays?.negative_days ?? '--', tone: 'down', icon: 'metrics-risk' },
    { label: 'Avg negative day', value: signedUsd(tradingDays?.avg_negative_days), tone: 'down', icon: 'stat-rule' },
  ];

  // 5. Results by Position Size (Lots)
  const lotsBarData = useMemo(() => {
    return Object.keys(resultsByLots).map((lot) => ({
      label: t('{{lot}} lot', { lot }),
      value: Number(resultsByLots[lot]?.results) || 0,
    }));
  }, [resultsByLots]);

  const lotsTableRows = useMemo(() => {
    return Object.keys(resultsByLots).map((lot) => ({
      id: lot,
      lots: t('{{lot}} lot', { lot }),
      result: Number(resultsByLots[lot]?.results) || 0,
    }));
  }, [resultsByLots]);

  const lotsColumns = [
    { key: 'lots', header: 'Position Size', width: 1 },
    {
      key: 'result',
      header: 'Result',
      width: 1,
      tone: (r) => (r.result >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.result),
    },
  ];

  // 6. Results by Trade Duration
  const durationBarData = useMemo(() => {
    const chart = tradeDuration?.chart;
    const labels = chart?.duration ?? [];
    const values = chart?.average_profit_loss ?? [];
    return labels.map((dur, i) => ({
      label: dur,
      value: Number(values[i]) || 0,
    }));
  }, [tradeDuration?.chart]);

  const durationTableRows = useMemo(() => {
    const list = Array.isArray(tradeDuration?.data) ? tradeDuration.data : [];
    return list.map((item, i) => ({
      id: String(i),
      duration: item?.Duration ?? '--',
      trades: item?.no_of_trade ?? 0,
      results: Number(item?.results) || 0,
    }));
  }, [tradeDuration?.data]);

  const durationColumns = [
    { key: 'duration', header: 'Duration Bucket', width: 1.2 },
    { key: 'trades', header: 'Trades', width: 0.8 },
    {
      key: 'results',
      header: 'Net Result',
      width: 1,
      tone: (r) => (r.results >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.results),
    },
  ];

  // 7. Results by Open Hour
  const openHourBarData = useMemo(() => {
    const chart = openHour?.chart_data;
    const labels = chart?.duration ?? [];
    const values = chart?.average_profit_loss ?? [];
    return labels.map((h, i) => ({
      label: h,
      value: Number(values[i]) || 0,
    }));
  }, [openHour?.chart_data]);

  const openHourTableRows = useMemo(() => {
    const list = Array.isArray(openHour?.data) ? openHour.data : [];
    return list.map((item, i) => ({
      id: String(i),
      hour: item?.Duration ?? '--',
      trades: item?.no_of_trade ?? 0,
      results: Number(item?.results) || 0,
    }));
  }, [openHour?.data]);

  const openHourColumns = [
    { key: 'hour', header: 'Hour Window', width: 1.2 },
    { key: 'trades', header: 'Trades', width: 0.8 },
    {
      key: 'results',
      header: 'Net Result',
      width: 1,
      tone: (r) => (r.results >= 0 ? 'up' : 'down'),
      render: (r) => signedUsd(r.results),
    },
  ];

  return (
    <div className="am-analysis-subsection">
      {/* Sub-nav Navigation / Selector */}
      <div className="am-analysis-subnav">
        <SegmentedTabs
          size="lg"
          radius="tab-track"
          options={SUB_TABS}
          value={activeSubTab}
          onChange={setActiveSubTab}
        />
      </div>

      {/* Main Analysis Display Area */}
      <div className="am-analysis-subcontent">
        {/* SUB-VIEW 1: Long / Short Balance */}
        {activeSubTab === SUB_TABS[0] && (
          <div className="am-analysis-view">
            {/* Long vs Short Comparison Cards */}
            <div className="am-analysis-lscards">
              {lsCards.map((c) => (
                <div key={c.title} className="am-analysis-lscard">
                  <div className="am-analysis-lscard__head">
                    <span className="am-analysis-lscard__title">{t(c.title)}</span>
                    <span className="am-analysis-lscard__icon-wrap">
                      <span className="icon am-analysis-lscard__icon" style={icon(c.icon)} />
                    </span>
                  </div>
                  <div className="am-analysis-lscard__body">
                    <div className="am-analysis-lscard__side">
                      <span className="am-analysis-lscard__pill is-long">{t('Long')}</span>
                      <span className={`am-analysis-lscard__val${c.longTone ? ` is-${c.longTone}` : ''}`}>
                        {c.long}
                      </span>
                    </div>
                    <div className="am-analysis-lscard__side">
                      <span className="am-analysis-lscard__pill is-short">{t('Short')}</span>
                      <span className={`am-analysis-lscard__val${c.shortTone ? ` is-${c.shortTone}` : ''}`}>
                        {c.short}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dual Balance Curves */}
            <div className="am-analysis__row">
              <section className="am-card am-analysis__card">
                <header className="am-card__head">
                  <span className="am-card__title">
                    <span className="icon am-card__icon" style={icon('metrics-direction')} />
                    {t('Long Balance Curve')}
                  </span>
                  <span className="am-card__meta">{t('Long trades performance')}</span>
                </header>
                <div className="am-analysis__chart-wrap">
                  {longCurveData.length === 0 ? (
                    <div className="am-analysis__empty-chart">
                      <p>{t('No long trade curve recorded')}</p>
                    </div>
                  ) : (
                    <LineChart
                      data={longCurveData}
                      height={220}
                      lineColor="#004bff"
                      gradientFrom="rgba(0, 75, 255, 0.35)"
                      gradientTo="rgba(0, 75, 255, 0.02)"
                      showXAxis
                      showYAxis
                      yAxisFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                  )}
                </div>
              </section>

              <section className="am-card am-analysis__card">
                <header className="am-card__head">
                  <span className="am-card__title">
                    <span className="icon am-card__icon" style={icon('metrics-direction')} />
                    {t('Short Balance Curve')}
                  </span>
                  <span className="am-card__meta">{t('Short trades performance')}</span>
                </header>
                <div className="am-analysis__chart-wrap">
                  {shortCurveData.length === 0 ? (
                    <div className="am-analysis__empty-chart">
                      <p>{t('No short trade curve recorded')}</p>
                    </div>
                  ) : (
                    <LineChart
                      data={shortCurveData}
                      height={220}
                      lineColor="#7b8dff"
                      gradientFrom="rgba(123, 141, 255, 0.35)"
                      gradientTo="rgba(123, 141, 255, 0.02)"
                      showXAxis
                      showYAxis
                      yAxisFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                  )}
                </div>
              </section>
            </div>
          </div>
        )}

        {/* SUB-VIEW 2: Results by Days */}
        {activeSubTab === SUB_TABS[1] && (
          <div className="am-analysis-view">
            <div className="am-analysis__row">
              <section className="am-card am-analysis__card">
                <header className="am-card__head">
                  <span className="am-card__title">
                    <span className="icon am-card__icon" style={icon('metrics-calendar')} />
                    {t('Results by Open')}
                  </span>
                  <span className="am-card__meta">{t('P&L by open day')}</span>
                </header>
                <div className="am-analysis__chart-wrap">
                  <AnalysisBarChart data={daysBarDataOpen} height={200} colorScheme="pnl" />
                </div>
              </section>

              <section className="am-card am-analysis__card">
                <header className="am-card__head">
                  <span className="am-card__title">
                    <span className="icon am-card__icon" style={icon('metrics-calendar')} />
                    {t('Results by Close')}
                  </span>
                  <span className="am-card__meta">{t('P&L by close day')}</span>
                </header>
                <div className="am-analysis__chart-wrap">
                  <AnalysisBarChart data={daysBarDataClose} height={200} colorScheme="pnl" />
                </div>
              </section>
            </div>

            <DataTable
              title="Daily Results Breakdown"
              icon="metrics-calendar"
              columns={daysColumns}
              rows={daysTableRows}
              getRowId={(r) => r.id}
              pagination={false}
              pageSize={7}
            />
          </div>
        )}

        {/* SUB-VIEW 3: Long / Short Comparison (Instruments) */}
        {activeSubTab === SUB_TABS[2] && (
          <div className="am-analysis-view">
            <section className="am-card am-analysis__card">
              <header className="am-card__head">
                <span className="am-card__title">
                  <span className="icon am-card__icon" style={icon('metrics-instruments')} />
                  {t('P&L by Traded Instrument')}
                </span>
                <span className="am-card__meta">{t('Symbol distribution')}</span>
              </header>
              <div className="am-analysis__chart-wrap">
                <AnalysisBarChart
                  data={instrumentBarData}
                  height={220}
                  colorScheme="pnl"
                  tooltipFormatter={(d) => `${t(d.label)}: ${signedUsd(d.value)}`}
                />
              </div>
            </section>

            <DataTable
              title="Instrument Performance Summary"
              icon="metrics-instruments"
              columns={instrumentColumns}
              rows={instrumentTableRows}
              getRowId={(r) => r.id}
              pagination={false}
              pageSize={Math.max(instrumentTableRows.length, 1)}
              emptyMessage="No instruments traded on this account"
            />
          </div>
        )}

        {/* SUB-VIEW 4: Trading Days Analysis */}
        {activeSubTab === SUB_TABS[3] && (
          <div className="am-analysis-view">
            <div className="am-analysis__stat-grid">
              {tradingDayCards.map((c) => (
                <div key={c.label} className="am-analysis__stat-card">
                  <div className="am-analysis__stat-top">
                    <span className="icon am-analysis__stat-icon" style={icon(c.icon)} />
                    <span className="am-analysis__stat-label">{t(c.label)}</span>
                  </div>
                  <span className={`am-analysis__stat-value${c.tone ? ` is-${c.tone}` : ''}`}>
                    {c.value}
                  </span>
                </div>
              ))}
            </div>

            <section className="am-card am-analysis__card">
              <header className="am-card__head">
                <span className="am-card__title">
                  <span className="icon am-card__icon" style={icon('trade-history-trend')} />
                  {t('Daily Performance Distribution')}
                </span>
                <span className="am-card__meta">{t('Win vs Loss day consistency')}</span>
              </header>
              <div className="am-analysis__chart-wrap">
                <AnalysisBarChart
                  data={daysBarDataClose}
                  height={220}
                  colorScheme="pnl"
                  tooltipFormatter={(d) => `${t(d.label)}: ${signedUsd(d.value)}`}
                />
              </div>
            </section>
          </div>
        )}

        {/* SUB-VIEW 5: Results by Position Size */}
        {activeSubTab === SUB_TABS[4] && (
          <div className="am-analysis-view">
            <section className="am-card am-analysis__card">
              <header className="am-card__head">
                <span className="am-card__title">
                  <span className="icon am-card__icon" style={icon('stat-balance')} />
                  {t('P&L by Position Size (Lots)')}
                </span>
                <span className="am-card__meta">{t('Volume tier efficiency')}</span>
              </header>
              <div className="am-analysis__chart-wrap">
                <AnalysisBarChart
                  data={lotsBarData}
                  height={220}
                  colorScheme="pnl"
                  tooltipFormatter={(d) => `${t(d.label)}: ${signedUsd(d.value)}`}
                />
              </div>
            </section>

            <DataTable
              title="Position Size Tiers"
              icon="stat-balance"
              columns={lotsColumns}
              rows={lotsTableRows}
              getRowId={(r) => r.id}
              pagination={false}
              pageSize={Math.max(lotsTableRows.length, 1)}
              emptyMessage="No lot size data available"
            />
          </div>
        )}

        {/* SUB-VIEW 6: Results by Trade Duration */}
        {activeSubTab === SUB_TABS[5] && (
          <div className="am-analysis-view">
            <section className="am-card am-analysis__card">
              <header className="am-card__head">
                <span className="am-card__title">
                  <span className="icon am-card__icon" style={icon('metrics-session')} />
                  {t('Average P&L by Trade Duration')}
                </span>
                <span className="am-card__meta">{t('Holding time vs return')}</span>
              </header>
              <div className="am-analysis__chart-wrap">
                <AnalysisBarChart
                  data={durationBarData}
                  height={220}
                  colorScheme="pnl"
                  tooltipFormatter={(d) => `${t(d.label)}: ${signedUsd(d.value)}`}
                />
              </div>
            </section>

            <DataTable
              title="Duration Breakdown"
              icon="metrics-session"
              columns={durationColumns}
              rows={durationTableRows}
              getRowId={(r) => r.id}
              pagination={false}
              pageSize={Math.max(durationTableRows.length, 1)}
              emptyMessage="No trade duration records available"
            />
          </div>
        )}

        {/* SUB-VIEW 7: Results by Open Hour */}
        {activeSubTab === SUB_TABS[6] && (
          <div className="am-analysis-view">
            <section className="am-card am-analysis__card">
              <header className="am-card__head">
                <span className="am-card__title">
                  <span className="icon am-card__icon" style={icon('metrics-account')} />
                  {t('Average P&L by Open Hour')}
                </span>
                <span className="am-card__meta">{t('Time of day distribution (UTC)')}</span>
              </header>
              <div className="am-analysis__chart-wrap">
                <AnalysisBarChart
                  data={openHourBarData}
                  height={220}
                  colorScheme="pnl"
                  tooltipFormatter={(d) => `${t(d.label)}: ${signedUsd(d.value)}`}
                />
              </div>
            </section>

            <DataTable
              title="Hourly Trade Results"
              icon="metrics-account"
              columns={openHourColumns}
              rows={openHourTableRows}
              getRowId={(r) => r.id}
              pagination={false}
              pageSize={Math.max(openHourTableRows.length, 1)}
              emptyMessage="No hourly data available"
            />
          </div>
        )}
      </div>
    </div>
  );
}
